// @ts-nocheck

import React, { memo, useState, useEffect } from 'react';
import { Handle, Position, useViewport } from '@xyflow/react';
import { cn } from '@/lib/utils';
import * as Tooltip from '@radix-ui/react-tooltip';
import { EntitreeNode, Pass2Sidequest } from '../../../typesV2';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { TooltipContent } from '../components/TooltipContent';
import { slugify } from '@/lib/map/slugify';
import { useAtom, useAtomValue } from 'jotai';
import { contentAtom, isLoadingNavAtom, isStreamingMapAtom, mobileSidebar } from '../../../jotaiStore';
import { showNavigationWarningToast, dismissNavigationWarningToast } from '@/lib/toast';
import LoadingSpinnerSmall from '../../../features/global/loading/LoadingSpinnerSmall';
import getContentLink from '@/lib/map/getContentLink';
const { Top, Bottom, Left, Right } = Position;

const SideQuestLink = ({
  sideQuest,
  index,
  mapSlug,
}: {
  sideQuest: Pass2Sidequest;
  index: number;
  mapSlug: string;
}) => {
  const { x, y, zoom } = useViewport();
  const [, setIsLoadingNav] = useAtom(isLoadingNavAtom);
  const [, setContentData] = useAtom(contentAtom);
  const isStreamingMap = useAtomValue(isStreamingMapAtom);
  const [showMobileSidebar, setShowMobileSidebar] = useAtom(mobileSidebar);
  useEffect(() => {
    if (!isStreamingMap) {
      dismissNavigationWarningToast();
    }
  }, [isStreamingMap]);

  const { contentSlug } = useParams();
  const contentLink = getContentLink(x, y, zoom, contentSlug as string, mapSlug, sideQuest.sideQuestTitle);

  return (
    <Tooltip.Root delayDuration={100}>
      <Link   
        href={contentLink}
        className=""
        onClick={(e) => {
          if (isStreamingMap) {
            e.preventDefault();
            showNavigationWarningToast();
          } else {
            if (showMobileSidebar) setShowMobileSidebar(false);
            setIsLoadingNav(true);
            setContentData('');
          }
        }}
      >
        <Tooltip.Trigger asChild id={`sideQuest-${index}-tooltip`}>
          <div className="p-4 text-center transition-colors rounded-lg bg-shades-baseLighter hover:bg-shades-baseLightest focus:outline-none focus:ring-2 focus:ring-colors-lavender focus:ring-offset-2 focus:ring-offset-shades-base">
            <h3 className="text-2xl font-medium underline text-shades-textLight">
              {sideQuest.sideQuestTitle}
            </h3>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <TooltipContent
            title={sideQuest.sideQuestTitle}
            description={sideQuest.sideQuestDescription}
            mapSlug={mapSlug}
            labelText="OPTIONAL"
            side="right"
          />
        </Tooltip.Portal>
      </Link>
    </Tooltip.Root>
  );
};

const SideQuestNode = memo((node: EntitreeNode) => {
  const { data } = node;
  const params = useParams();
  const mapSlug = Array.isArray(params.mapSlug)
    ? params.mapSlug[0]
    : params.mapSlug || '';  
  return (
    <div
      className={cn(
        'relative w-full max-w-md rounded-xl bg-shades-base p-6 pt-10 border-[4px] border-shades-baseLighterist cursor-default'
      )}
    >
      {/* Target Handle */}
      <Handle
        type={'target'}
        position={node.targetPosition}
        id={node.targetPosition}
      />
      <div className="absolute px-6 py-2 text-xl font-medium tracking-wider -translate-x-1/2 rounded-full -top-6 left-1/2 border-2 bg-shades-base border-shades-baseLighterist text-shades-text">
        OPTIONAL
      </div>
      <div className="flex flex-col gap-4">
        {data?.sideQuests?.map((sidequest, index) => (
          <SideQuestLink
            sideQuest={sidequest}
            index={index}
            key={sidequest.sideQuestTitle}
            mapSlug={mapSlug}
          />
        ))}
      </div>
    </div>
  );
});

SideQuestNode.displayName = 'SideQuestNode';

export default SideQuestNode;
